import { isOk, Result } from "@pacote/result"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { z } from "zod"

import {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { withAuthClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type JumioPayload = {
  billCodeId: number
  condition: boolean
}

const jumioServiceResponseSchema = baseApiResponseSchema.extend({
  url: z.string(),
})

type JumioResponse = z.infer<typeof jumioServiceResponseSchema>

const jumioServiceErrorResponseSchema = baseApiErrorResponseSchema.extend({
  counterCheck: z.number().optional(),
  counterRefresh: z.number().optional(),
})

type JumioErrorResponse = z.infer<typeof jumioServiceErrorResponseSchema>

type JumioFetcherReturn = Result<JumioResponse, void>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const jumioServiceEndpoint = "/ocr-ms/ms/jumio/ext/info"
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const jumioFetcher = async (
  key: string,
  { arg }: { arg: JumioPayload },
): Promise<JumioFetcherReturn> => {
  const responseResult = await withAuthClientFetcher<
    JumioResponse,
    JumioErrorResponse
  >({
    url: key,
    method: "POST",
    body: { ...arg },
    successSchemaValidator: jumioServiceResponseSchema,
    errorSchemaValidator: jumioServiceErrorResponseSchema,
  })

  if (isOk(responseResult)) return responseResult

  throw new UnhandledApiError({
    status: ApiFailure.UNPROCESSABLE_ERROR,
    message: `An error occurred on request: ${key}`,
    data: responseResult.value,
    meta: responseResult.value.meta,
    originalStatus: responseResult.value.status,
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useJumioMutation = () => {
  const { data } = useSWR<JumioFetcherReturn>(jumioServiceEndpoint)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: _doNotUseData, ...rest } = useSWRMutation(
    jumioServiceEndpoint,
    jumioFetcher,
    { populateCache: true },
  )
  return { data, ...rest }
}

export { useJumioMutation }
